import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileText, Plus, ExternalLink, Calendar, Briefcase } from "lucide-react";
import { formatINR } from "@/lib/utils";
import { QuoteStatusBadge } from "@/pages/quotes/QuoteStatusBadge";
import type { QuotationRecord } from "@/types";

interface QuotesWidgetProps {
  quotes: QuotationRecord[];
  onNewQuote?: () => void;
  canCreate?: boolean;
}

export function QuotesWidget({ quotes, onNewQuote, canCreate }: QuotesWidgetProps) {
  const visible = quotes.filter((q) => q.quote_status !== "SUPERSEDED");
  const totalValue = visible.reduce((acc, q) => acc + (Number(q.grand_total) || 0), 0);

  return (
    <div className="bg-card border border-border/80 rounded-2xl shadow-xs overflow-hidden">
      {/* Top Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-border/80">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
            <FileText className="size-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold tracking-tight text-foreground">
                Quotations ({visible.length})
              </h3>
              {visible.length > 0 && (
                <span className="text-xs font-bold text-foreground font-mono bg-muted px-2 py-0.5 rounded-md">
                  {formatINR(totalValue)} Total
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Formal price quotations
            </p>
          </div>
        </div>

        {canCreate && onNewQuote && (
          <Button
            type="button"
            size="sm"
            onClick={onNewQuote}
            className="gap-1.5 self-start sm:self-center h-8 text-xs font-medium"
          >
            <Plus className="size-3.5" />
            <span>New Quote</span>
          </Button>
        )}
      </div>

      {/* Table / List */}
      {visible.length === 0 ? (
        <div className="p-8 text-center space-y-3">
          <div className="size-10 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
            <FileText className="size-5" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No quotes yet</p>
            <p className="text-xs text-muted-foreground">
              No quotations have been generated for this record.
            </p>
          </div>
          {canCreate && onNewQuote && (
            <Button type="button" variant="outline" size="sm" onClick={onNewQuote} className="gap-1.5 text-xs">
              <Plus className="size-3.5" />
              <span>Create Quote</span>
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead className="text-xs font-semibold">Quote #</TableHead>
                <TableHead className="text-xs font-semibold">Status</TableHead>
                <TableHead className="text-xs font-semibold">Grand Total</TableHead>
                <TableHead className="text-xs font-semibold">Associated Deal</TableHead>
                <TableHead className="text-xs font-semibold">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((q) => (
                <TableRow key={q.id} className="hover:bg-muted/40 transition-colors">
                  <TableCell className="font-medium text-xs py-3">
                    <Link
                      to={`/quotes/${q.id}`}
                      className="text-foreground hover:text-primary font-semibold font-mono hover:underline inline-flex items-center gap-1 group"
                    >
                      <span>{q.display_number || q.quote_number}</span>
                      <ExternalLink className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </TableCell>

                  <TableCell className="text-xs py-3">
                    <QuoteStatusBadge status={q.quote_status} />
                  </TableCell>

                  <TableCell className="text-xs py-3 font-semibold font-mono text-foreground">
                    {formatINR(q.grand_total)}
                  </TableCell>

                  <TableCell className="text-xs py-3">
                    {q.deal ? (
                      <Link
                        to={`/deals/${q.deal.id}`}
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 truncate max-w-[180px]"
                      >
                        <Briefcase className="size-3 shrink-0" />
                        <span className="truncate">{q.deal.name}</span>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  <TableCell className="text-xs text-muted-foreground py-3">
                    {q.created_on ? (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="size-3 text-muted-foreground/80" />
                        {new Date(q.created_on).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
